import { styled } from "@pigment-css/react";

const CHEVRON_SVG = `url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4 6L8 10L12 6' stroke='%23131313' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;

// Pill-shaped filter trigger used in the management-view filter bars (each filter is its own
// rounded chip with a chevron). Keep in sync with `FilterDropdownButton` in
// `SearchAndFilterBar/ManagementSearchAndFilterBar.tsx`.
export const FilterPillTrigger = styled("button")(({ theme }) => ({
  height: "50px",
  display: "inline-flex",
  alignItems: "center",
  gap: theme.managementView.spacing[8],
  padding: `0 ${theme.managementView.spacing[24]}`,
  backgroundColor: "transparent",
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: "50px",
  cursor: "pointer",
  transition: "background-color 0.2s",
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[440],
  color: theme.managementView.colors.neutral[56],
  whiteSpace: "nowrap",

  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },

  "&:focus-visible": {
    outline: "2px solid currentColor",
    outlineOffset: "-2px",
  },

  "&:disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },
}));

export const SelectTrigger = styled("button")(({ theme }) => ({
  flex: "1 1 auto",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  height: "100%",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[440],
  lineHeight: theme.managementView.font.lineHeight[164],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[90],
  backgroundColor: "transparent",
  border: "none",
  borderRight: `1px solid ${theme.managementView.colors.neutral[8]}`,
  padding: `0 ${theme.managementView.spacing[24]} 0 ${theme.managementView.spacing[16]}`,
  cursor: "pointer",
  appearance: "none",
  backgroundImage: CHEVRON_SVG,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 8px center",
  backgroundSize: "16px",
  minHeight: "48px",
  textAlign: "left",
  whiteSpace: "nowrap",

  "&:focus-visible": {
    outline: "2px solid currentColor",
    outlineOffset: "-2px",
  },

  "&:last-child": {
    borderRight: "none",
  },

  "&:disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },
}));

export const SelectPopover = styled("div")(({ theme }) => ({
  position: "fixed",
  maxHeight: "300px",
  // "overlay" is valid CSS but not in the TS CSSProperties type union
  overflowY: "overlay" as never,
  backgroundColor: theme.managementView.colors.neutral[0],
  border: "1px solid rgba(19, 19, 19, 0.04)",
  borderRadius: theme.managementView.radius[16],
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
  padding: theme.managementView.spacing[4],
  zIndex: 10010,
  scrollbarWidth: "thin",
  scrollbarColor: "transparent transparent",

  "&:hover": {
    scrollbarColor: "rgba(19, 19, 19, 0.32) transparent",
  },

  "&::-webkit-scrollbar": {
    width: "4px",
  },

  "&::-webkit-scrollbar-track": {
    background: "transparent",
    marginBlock: "8px",
  },

  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "transparent",
    borderRadius: "50px",
  },

  "&:hover::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(19, 19, 19, 0.32)",
  },
}));

export const SelectItem = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[8],
  height: "40px",
  margin: `0 ${theme.managementView.spacing[4]}`,
  padding: `0 ${theme.managementView.spacing[16]}`,
  borderRadius: theme.managementView.radius[16],
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[440],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[90],
  cursor: "pointer",
  userSelect: "none",

  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },
}));

export const ItemLabel = styled("span")(() => ({
  flex: "1 1 0",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

// When a select has more options than this, it renders a search input so the
// list stays navigable. Short dropdowns (operators, yes/no) stay search-free.
export const SELECT_SEARCH_THRESHOLD = 8;

// Flex-column variant of SelectPopover for searchable selects: the search input
// stays pinned while only the items scroll (see SelectItemsContainer).
export const SearchableSelectPopover = styled(SelectPopover)(() => ({
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
}));

export const SearchWrapper = styled("div")(({ theme }) => ({
  padding: `${theme.managementView.spacing[12]} ${theme.managementView.spacing[10]}`,
  flexShrink: 0,
}));

export const SearchInput = styled("input")(({ theme }) => ({
  width: "100%",
  height: "40px",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[440],
  color: theme.managementView.colors.neutral[90],
  backgroundColor: "transparent",
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: theme.managementView.radius[32],
  padding: `0 ${theme.managementView.spacing[18]}`,
  outline: "none",
  boxSizing: "border-box",

  "&::placeholder": {
    color: theme.managementView.colors.neutral[56],
  },
}));

export const SelectItemsContainer = styled("div")(({ theme }) => ({
  // "overlay" is valid CSS but not in the TS CSSProperties type union
  overflowY: "overlay" as never,
  padding: `${theme.managementView.spacing[4]} 0 ${theme.managementView.spacing[6]}`,
  scrollbarWidth: "thin",
  scrollbarColor: "transparent transparent",

  "&:hover": {
    scrollbarColor: "rgba(19, 19, 19, 0.32) transparent",
  },

  "&::-webkit-scrollbar": {
    width: "4px",
  },

  "&::-webkit-scrollbar-track": {
    background: "transparent",
    marginBlock: "8px",
  },

  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "transparent",
    borderRadius: "50px",
  },

  "&:hover::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(19, 19, 19, 0.32)",
  },
}));

export const SelectEmptyState = styled("div")(({ theme }) => ({
  padding: `${theme.managementView.spacing[12]} ${theme.managementView.spacing[18]}`,
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[440],
  color: theme.managementView.colors.neutral[56],
}));

export const CheckMark = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M2.5 7L5.5 10L11.5 4" stroke="#009912" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
